import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useEffect } from 'react';
import { Sidebar } from './presentation/components/layout/Sidebar';
import { DependencyProvider } from './presentation/contexts/DependencyContext';
import { AuthProvider, useAuth } from './presentation/contexts/AuthContext';
import { AppRoutes } from './presentation/routing/AppRoutes';
import { useState } from 'react';
import coopecLogo from './stories/assets/coopec.png';
import bioTransacLogo from './stories/assets/bioTransac_03.png';
import './app-ecrans.css';

/**
 * Splash de marque plein écran.
 *
 * Sert d'écran de transition unifié pendant l'initialisation de session ET
 * pendant la bascule vers Keycloak : au lieu d'un message « Redirection vers
 * Keycloak » et d'un flash de contenu, l'utilisateur voit un simple écran de
 * chargement aux couleurs de l'app. La redirection OIDC devient ainsi
 * imperceptible — elle ressemble au démarrage normal de l'application.
 */
function BrandSplash({ label }: { label?: string }) {
  return (
    <div className="ae-fond" role="status" aria-live="polite">
      <img src={bioTransacLogo} alt="BIOTRANSAC" className="ae-logo" />
      <div className="ae-chargement">
        <span className="ae-roue" />
        {label && <span>{label}</span>}
      </div>
    </div>
  );
}

/** Déclenche la connexion OIDC et affiche le splash pendant la bascule. */
function AutoLogin() {
  const { login } = useAuth();

  useEffect(() => {
    // La redirection démarre immédiatement ; le splash reste affiché
    // jusqu'à ce que le navigateur quitte la page.
    void login();
  }, [login]);

  return <BrandSplash label="Connexion sécurisée…" />;
}

/** Écran de confirmation de déconnexion, avec bouton de reconnexion. */
function LoggedOutScreen() {
  const { login } = useAuth();
  const [reconnecting, setReconnecting] = useState(false);

  if (reconnecting) return <BrandSplash label="Connexion sécurisée…" />;

  return (
    <div className="ae-fond">
      <main className="ae-carte">
        <img src={bioTransacLogo} alt="BIOTRANSAC" className="ae-logo" />
        <div className="ae-pastille" aria-hidden="true">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1>Vous êtes déconnecté</h1>
        <p>Votre session BIOTRANSAC est fermée en toute sécurité.</p>
        <button
          type="button"
          autoFocus
          onClick={() => {
            sessionStorage.removeItem('biotransac.logging_out');
            setReconnecting(true);
            void login();
          }}
          className="ae-btn"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3" />
          </svg>
          Se reconnecter
        </button>
        <p className="ae-note">Sur un poste partagé, vous pouvez fermer cette fenêtre.</p>
      </main>
    </div>
  );
}

function AuthenticatedShell() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <div className="ml-64">
        <header className="bg-white text-gray-900 p-6 shadow-md border-b border-gray-200">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold">BIOTRANSAC - Gestion de Trésorerie</h1>
              <p className="text-gray-600 mt-1 text-sm">Plateforme digitale de gestion des transactions en espèces</p>
            </div>
            <img
              src={coopecLogo}
              alt="COOPEC Logo"
              className="h-12 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))' }}
            />
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-6">
          <AppRoutes />
        </main>
      </div>
    </div>
  );
}

function AppShell() {
  const { status } = useAuth();
  const loggingOut = sessionStorage.getItem('biotransac.logging_out') === 'true';

  if (status === 'authenticated') return <AuthenticatedShell />;

  // Déconnexion explicite : on ne reconnecte pas automatiquement.
  if (loggingOut) return <LoggedOutScreen />;

  // Pendant l'initialisation de session, on affiche un splash neutre sans
  // déclencher de redirection : si un jeton valide existe, la bascule vers
  // l'app est instantanée et Keycloak n'entre jamais en jeu.
  if (status === 'initializing') return <BrandSplash label="Chargement…" />;

  // Non authentifié : bascule silencieuse vers Keycloak, derrière le splash.
  return <AutoLogin />;
}

export default function App() {
  return (
    <DependencyProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </DependencyProvider>
  );
}
